import React, { useEffect, useRef, useState } from "react";
import { NewsDetailData } from "../../../pages/NewsDetail/types";
import { NewsDetailImage } from "./NewsDetailImage";
import { NewsDetailHeader } from "./NewsDetailHeader";
import { NewsDetailContent } from "./NewsDetailContent";
import { SwiperNavigationButtons } from "./SwiperNavigationButtons";
import { isOpenInApp as checkIsOpenInApp } from "@repo/shared-utils/pwa-utils";

interface NewsDetailMobileProps {
  newsData: NewsDetailData;
  showLeftButton: boolean;
  showRightButton: boolean;
  onScrollPrev: () => void;
  onScrollNext: () => void;
}

export const NewsDetailMobile: React.FC<NewsDetailMobileProps> = ({
  newsData,
  showLeftButton,
  showRightButton,
  onScrollPrev,
  onScrollNext,
}) => {
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchStartTime = useRef<number | null>(null);
  const minSwipeDistance = 30; // Minimum distance in pixels to trigger a swipe
  const maxSwipeTime = 600; // Maximum time in milliseconds for a swipe gesture

  const [isOpenInApp, setIsOpenInApp] = useState(false);

  useEffect(() => {
    // Determine if the page is opened inside the mobile app
    // This uses shared pwa-utils which checks URL params, cookies, etc.
    try {
      setIsOpenInApp(checkIsOpenInApp());
    } catch {
      setIsOpenInApp(false);
    }
  }, []);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0];
    if (touch) {
      touchStartX.current = touch.clientX;
      touchStartY.current = touch.clientY;
      touchStartTime.current = Date.now();
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null || touchStartY.current === null || touchStartTime.current === null) {
      return;
    }

    const touch = e.changedTouches[0];
    if (!touch) {
      touchStartX.current = null;
      touchStartY.current = null;
      touchStartTime.current = null;
      return;
    }

    const deltaX = touchStartX.current - touch.clientX;
    const deltaY = touchStartY.current - touch.clientY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    const deltaTime = Date.now() - touchStartTime.current;

    // Swipe detection:
    // 1. Horizontal movement must be greater than vertical (allows some vertical movement)
    // 2. Minimum distance threshold
    // 3. Reasonable time limit
    const isHorizontalSwipe = absDeltaX > absDeltaY;
    const meetsDistanceThreshold = absDeltaX > minSwipeDistance;
    const meetsTimeThreshold = deltaTime < maxSwipeTime;

    if (isHorizontalSwipe && meetsDistanceThreshold && meetsTimeThreshold) {
      // Prevent default to avoid any conflicts
      e.preventDefault();
      e.stopPropagation();
      
      if (deltaX > 0 && showRightButton) {
        // Swiped left - go to next article
        onScrollNext();
      } else if (showLeftButton) {
        // Swiped right - go to previous article
        onScrollPrev();
      }
    }

    // Reset touch start positions
    touchStartX.current = null;
    touchStartY.current = null;
    touchStartTime.current = null;
  };

  return (
    <div className="content-wrapper max-w-[1328px] mx-auto bg-white flex flex-col">
      <div
        className="relative py-3 px-4 gap-3 bg-white"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {/* News Content */}
        <div className="gap-3 flex flex-col">
          {/* News Image */}
          <NewsDetailImage newsData={newsData} />

          {/* Source, Headline, Timestamp, Stock ticker */}
          <NewsDetailHeader newsData={newsData} />

          {/* Article Body */}
          <NewsDetailContent newsData={newsData} />

        </div>

        {/* Swiper Navigation Buttons */}
        <SwiperNavigationButtons
          showLeftButton={showLeftButton}
          showRightButton={showRightButton}
          onScrollPrev={onScrollPrev}
          onScrollNext={onScrollNext}
          isMobile={true}
          isOpenInApp={isOpenInApp}
          headline={newsData.headline}
          componentPosition="1"
        />
      </div>
    </div>
  );
};

